import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { Bar } from "react-chartjs-2";
import { MoreVertical } from "react-feather";
import { rgba } from "polished";

import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  IconButton,
} from "@mui/material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);

const ChartWrapper = styled.div`
  height: 340px;
  width: 100%;
`;

const BarChart = ({ theme, reports = [], labelFormatter }) => {
  const fmtLabel =
    typeof labelFormatter === "function"
      ? labelFormatter
      : (d) => String(d ?? "");

  const locations = Array.from(
    new Set(
      reports
        .flatMap((r) =>
          Array.isArray(r?.locationStats) ? r.locationStats : []
        )
        .map((s) => s?.location)
        .filter(Boolean)
    )
  );

  const labels = reports.map((r) => fmtLabel(r?.date));

  const palette = [
    theme.palette.success.main,
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
  ];

  const datasets = locations.map((loc, i) => {
    const base = palette[i % palette.length];
    return {
      label: loc,
      backgroundColor: rgba(base, 0.75),
      borderColor: base,
      hoverBackgroundColor: rgba(base, 0.9),
      hoverBorderColor: base,
      data: reports.map((r) => {
        const stats = Array.isArray(r?.locationStats) ? r.locationStats : [];
        const found = stats.find((s) => String(s?.location) === String(loc));
        return Number(found?.totalItems) || 0;
      }),
      barPercentage: 0.6,
      categoryPercentage: 0.7,
      borderRadius: 6,
    };
  });

  const data = { labels, datasets };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        grid: {
          display: false,
        },
        stacked: true,
        ticks: {
          stepSize: 20,
          fontColor: theme.palette.text.secondary,
        },
      },

      x: {
        stacked: true,
        grid: {
          color: "transparent",
        },
        ticks: {
          fontColor: theme.palette.text.secondary,
        },
      },
    },
  };

  return (
    <Card mb={6}>
      <CardHeader
        action={
          <IconButton aria-label="settings" size="large">
            <MoreVertical />
          </IconButton>
        }
        title="Items tested by location (last 7 reports)"
      />

      <CardContent>
        <ChartWrapper>
          <Bar data={data} options={options} />
        </ChartWrapper>
      </CardContent>
    </Card>
  );
};

export default withTheme(BarChart);
